import React, { createContext, useContext, useState, useEffect, useRef, ReactNode as ReactNodeType, useCallback } from 'react';
import { getUserInfo, saveUserInfo } from '@/services/userService';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ColorScheme = 'blue' | 'purple' | 'green' | 'orange' | 'pink' | 'red' | 'default';

interface ThemeContextType {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  primaryColor: ColorScheme;
  setPrimaryColor: (color: ColorScheme) => void;
  accentColor: ColorScheme;
  setAccentColor: (color: ColorScheme) => void;
  isDark: boolean;
}

const defaultContext: ThemeContextType = {
  mode: 'system',
  setMode: () => {},
  primaryColor: 'default',
  setPrimaryColor: () => {},
  accentColor: 'default',
  setAccentColor: () => {},
  isDark: false,
};

export const ThemeContext = createContext<ThemeContextType>(defaultContext);

export const useTheme = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: ReactNodeType;
}

// Color values in oklch format for each color scheme with light/dark variants
export const colorValues = {
  default: {
    light: {
      primary: 'oklch(0.76 0.1638 64.24)',
      accent: 'oklch(0.76 0.1638 64.24)',
    },
    dark: {
      primary: 'oklch(0.85 0.2 64.24)',
      accent: 'oklch(0.9 0.2 64.24)',
    }
  },
  blue: {
    light: {
      primary: 'oklch(0.55 0.18 240)',
      accent: 'oklch(0.76 0.18 240)',
    },
    dark: {
      primary: 'oklch(0.65 0.18 240)',
      accent: 'oklch(0.9 0.18 240)',
    }
  },
  purple: {
    light: {
      primary: 'oklch(0.55 0.22 300)',
      accent: 'oklch(0.76 0.22 300)',
    },
    dark: {
      primary: 'oklch(0.65 0.22 300)',
      accent: 'oklch(0.9 0.22 300)',
    }
  },
  green: {
    light: {
      primary: 'oklch(0.55 0.18 150)',
      accent: 'oklch(0.76 0.18 150)',
    },
    dark: {
      primary: 'oklch(0.65 0.18 150)',
      accent: 'oklch(0.9 0.18 150)',
    }
  },
  orange: {
    light: {
      primary: 'oklch(0.55 0.18 60)',
      accent: 'oklch(0.76 0.18 60)',
    },
    dark: {
      primary: 'oklch(0.65 0.18 60)',
      accent: 'oklch(0.9 0.18 60)',
    }
  },
  pink: {
    light: {
      primary: 'oklch(0.55 0.22 330)',
      accent: 'oklch(0.76 0.22 330)',
    },
    dark: {
      primary: 'oklch(0.65 0.22 330)',
      accent: 'oklch(0.9 0.22 330)',
    }
  },
  red: {
    light: {
      primary: 'oklch(0.55 0.22 20)',
      accent: 'oklch(0.76 0.22 20)',
    },
    dark: {
      primary: 'oklch(0.65 0.22 20)',
      accent: 'oklch(0.9 0.22 20)',
    }
  },
};

export function ThemeProvider({ children }: ThemeProviderProps) {
  // Default theme state - prioritize localStorage to avoid system override
  const getInitialMode = (): ThemeMode => {
    if (typeof window !== 'undefined') {
      // Check if we need to restore original theme after leaving questionnaire
      const originalTheme = sessionStorage.getItem('original-theme-mode') as ThemeMode;
      if (originalTheme && ['light', 'dark', 'system'].includes(originalTheme)) {
        // Clear it after retrieving to prevent future interference
        sessionStorage.removeItem('original-theme-mode');
        return originalTheme;
      }
      
      const savedMode = localStorage.getItem('theme-mode') as ThemeMode;
      if (savedMode && ['light', 'dark', 'system'].includes(savedMode)) {
        return savedMode;
      }
    }
    // Default new users to light mode when no preference is stored
    return 'light';
  };

  const getInitialColor = (key: string): ColorScheme => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(`primary-color` === key ? 'primary-color' : 'accent-color') as ColorScheme;
      if (saved && ['default', 'blue', 'purple', 'green', 'orange', 'pink', 'red'].includes(saved)) {
        return saved;
      }
    }
    return 'default';
  };

  const [mode, setMode] = useState<ThemeMode>(getInitialMode());
  const [primaryColor, setPrimaryColor] = useState<ColorScheme>(getInitialColor('primary'));
  const [accentColor, setAccentColor] = useState<ColorScheme>(getInitialColor('accent'));
  const [isLoading, setIsLoading] = useState(true);
  
  // Load theme preferences from user profile or localStorage
  const loadThemePreferences = useCallback(async () => {
    try {
      // First try to get from user profile
      const user = await getUserInfo() as any; // Temporary any to bypass type checking
      if (user?.theme) {
        console.log('Loading theme preferences from user profile:', user.theme);
        // Only update if user theme is different from current localStorage values
        const currentLocalMode = localStorage.getItem('theme-mode') as ThemeMode;
        const currentLocalPrimary = localStorage.getItem('primary-color') as ColorScheme;
        const currentLocalAccent = localStorage.getItem('accent-color') as ColorScheme;
        
        if (user.theme.mode && user.theme.mode !== currentLocalMode) {
          setMode(user.theme.mode);
        }
        if (user.theme.primaryColor && user.theme.primaryColor !== currentLocalPrimary) {
          setPrimaryColor(user.theme.primaryColor);
        }
        if (user.theme.accentColor && user.theme.accentColor !== currentLocalAccent) {
          setAccentColor(user.theme.accentColor);
        }
      } else {
        console.log('No user theme found, using localStorage values');
      }
    } catch (error) {
      console.error('Failed to load theme preferences from user profile:', error);
      console.log('Using localStorage values as fallback');
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Load theme preferences on mount
  useEffect(() => {
    loadThemePreferences();
  }, [loadThemePreferences]);

  const [isDark, setIsDark] = useState(false);
  
  // Track previous theme values to detect changes
  const prevThemeRef = useRef<{
    mode: ThemeMode;
    primaryColor: ColorScheme;
    accentColor: ColorScheme;
  } | null>(null);

  // Debug wrapper for setMode to track all theme changes
  const debugSetMode = useCallback((newMode: ThemeMode) => {
    console.log('ThemeContext.setMode called:', {
      from: mode,
      to: newMode,
      stackTrace: new Error().stack?.split('\n').slice(1, 8).join('\n'),
      timestamp: new Date().toISOString()
    });
    setMode(newMode);
  }, [mode]);

  // Save theme preferences to localStorage and user profile when they change
  useEffect(() => {
    if (isLoading) return;
    
    // Only proceed if theme values have actually changed
    const currentTheme = { mode, primaryColor, accentColor };
    if (prevThemeRef.current && 
        prevThemeRef.current.mode === currentTheme.mode &&
        prevThemeRef.current.primaryColor === currentTheme.primaryColor &&
        prevThemeRef.current.accentColor === currentTheme.accentColor) {
      return; // No changes to save
    }
    
    console.log('Saving theme preferences:', currentTheme);
    
    // Update localStorage
    localStorage.setItem('theme-mode', mode);
    localStorage.setItem('primary-color', primaryColor);
    localStorage.setItem('accent-color', accentColor);
    
    // Update user profile in the background
    const updateUserTheme = async () => {
      try {
        const user = await getUserInfo();
        if (user) {
          // Only save if the theme is different from the current user theme
          const userTheme = (user as any)?.theme || {};
          if (userTheme.mode !== mode || 
              userTheme.primaryColor !== primaryColor || 
              userTheme.accentColor !== accentColor) {
            
            console.log('Updating user theme in profile');
            await saveUserInfo({
              theme: {
                mode,
                primaryColor,
                accentColor
              }
            });
          } else {
            console.log('Theme matches user profile, skipping save');
          }
        }
      } catch (error) {
        console.error('Failed to update user theme:', error);
      }
    };
    
    updateUserTheme();
    prevThemeRef.current = currentTheme;
  }, [mode, primaryColor, accentColor, isLoading]);

  // Handle keyboard shortcut for theme toggle (Cmd + Shift + D)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'd' && e.metaKey && e.shiftKey) {
        e.preventDefault();
        const newMode = mode === 'dark' ? 'light' : mode === 'light' ? 'dark' : 'dark';
        console.log('Keyboard shortcut: changing mode from', mode, 'to', newMode);
        debugSetMode(newMode);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mode, debugSetMode]); // Include mode and debugSetMode in dependencies

  // Consolidated localStorage updates - single effect to prevent race conditions
  useEffect(() => {
    if (isLoading) return; // Don't save to localStorage while loading
    
    console.log('Updating localStorage:', { mode, primaryColor, accentColor });
    localStorage.setItem('theme-mode', mode);
    localStorage.setItem('primary-color', primaryColor);
    localStorage.setItem('accent-color', accentColor);
  }, [mode, primaryColor, accentColor, isLoading]);

  // Apply theme mode and colors together to prevent race conditions
  useEffect(() => {
    if (isLoading) return; // Don't apply theme while loading
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const applyTheme = () => {
      const prefersDark = mediaQuery.matches;
      const shouldBeDark = mode === 'dark' || (mode === 'system' && prefersDark);
      const themeVariant = shouldBeDark ? 'dark' : 'light';
      
      console.log('Applying theme:', { mode, prefersDark, shouldBeDark, themeVariant });
      
      // Update dark mode class
      document.documentElement.classList.toggle('dark', shouldBeDark);
      
      // Update isDark from the DOM class so external toggles are reflected
      const domHasDark = document.documentElement.classList.contains('dark');
      setIsDark(domHasDark);
      
      // Set CSS variables for primary color with !important to override global.css
      const primaryColorValue = colorValues[primaryColor][themeVariant].primary;
      const primaryForeground = shouldBeDark ? 'oklch(0.15 0.005 0)' : 'oklch(0.98 0.005 0)';
      
      // Create a style element for our dynamic theme overrides
      let styleElement = document.getElementById('dynamic-theme-overrides');
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = 'dynamic-theme-overrides';
        document.head.appendChild(styleElement);
      }
      
      // Set styles with !important to override global.css
      styleElement.textContent = `
        :root, :root.dark, .page-container, .page-container.dark {
          --primary: ${primaryColorValue} !important;
          --primary-foreground: ${primaryForeground} !important;
          --accent: ${colorValues[accentColor][themeVariant].accent} !important;
          --accent-foreground: ${shouldBeDark ? 'oklch(0.98 0.005 0)' : 'oklch(0.15 0.005 0)'} !important;
        }
      `;
    };

    applyTheme();

    // Only listen for system preference changes when mode is 'system'
    const listener = () => {
      if (mode === 'system') {
        console.log('System preference changed, reapplying theme');
        applyTheme();
      }
    };
    
    mediaQuery.addEventListener('change', listener);
    
    return () => {
      mediaQuery.removeEventListener('change', listener);
    };
  }, [mode, primaryColor, accentColor, isLoading]); // Include isLoading in dependencies
  
  // Keep isDark in sync with the <html> class regardless of the source of truth
  useEffect(() => {
    const target = document.documentElement;

    const updateFromDom = () => {
      const hasDark = target.classList.contains('dark');
      setIsDark(hasDark);
    };

    // Initialize on mount
    updateFromDom();

    // Observe future changes to the class attribute
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName === 'class') {
          updateFromDom();
        }
      }
    });

    observer.observe(target, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const value = {
    mode,
    setMode: debugSetMode,
    primaryColor,
    setPrimaryColor,
    accentColor,
    setAccentColor,
    isDark,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}
